import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export type DialogTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONE_ICON_CLASSES: Record<DialogTone, string> = {
  neutral: 'bg-phx-surface-2 text-phx-text-2',
  primary: 'bg-phx-primary-tint text-phx-primary',
  success: 'bg-phx-success-tint text-phx-success',
  warning: 'bg-phx-warning-tint text-phx-warning',
  danger: 'bg-phx-danger-tint text-phx-danger',
  info: 'bg-phx-info-tint text-phx-info',
};

export type DialogHeaderVariant = 'default' | 'primary';

export interface DialogHeaderProps {
  title?: ReactNode;
  /** Krátký popisek na stejném řádku jako titulek (nezalamuje se). */
  subtitle?: ReactNode;
  /** Víceřádkový popis pod titulkem (např. potvrzovací otázka). */
  description?: ReactNode;
  icon?: PhxIconName;
  tone?: DialogTone;
  /** Accent title bar (theme primary) — e.g. Pick records modal. */
  variant?: DialogHeaderVariant;
  /** Renders the close button when provided. */
  onClose?: () => void;
  closeLabel?: string;
  className?: string;
}

/**
 * Sdílená hlavička dialogu (ikona v tónované destičce, titulek, volitelný inline podtitulek nebo popis pod
 * titulkem, zavírací tlačítko). Kompaktní pruh s dolní linkou – vypadá stejně, ať ji vykreslí vestavěný
 * {@link import('./Dialog').Dialog}, nebo si ji vlastní `chromeless` dialog vloží sám.
 */
export function DialogHeader({
  title,
  subtitle,
  description,
  icon,
  tone = 'neutral',
  variant = 'default',
  onClose,
  closeLabel = 'Close',
  className,
}: DialogHeaderProps) {
  const primary = variant === 'primary';

  return (
    <header
      className={cn(
        'phx-dialog-head border-phx-border flex shrink-0 gap-2.5 border-b py-3 pr-3 pl-4',
        description ? 'items-start' : 'items-center',
        primary && 'bg-phx-primary text-phx-primary-fg',
        className
      )}
    >
      {icon && !primary ? (
        <span
          aria-hidden
          className={cn(
            'rounded-phx h-phx-control w-phx-control inline-flex shrink-0 items-center justify-center',
            TONE_ICON_CLASSES[tone]
          )}
        >
          <Icon name={icon} size={15} />
        </span>
      ) : null}
      {icon && primary ? (
        <span
          aria-hidden
          className="h-phx-control w-phx-control inline-flex shrink-0 items-center justify-center"
        >
          <Icon name={icon} size={15} className="text-phx-primary-fg" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="gap-phx-3_5 flex min-w-0 items-baseline">
          {title ? (
            <span
              className={cn(
                'text-phx-lg font-semibold whitespace-nowrap',
                primary ? 'text-phx-primary-fg' : 'text-phx-text'
              )}
            >
              {title}
            </span>
          ) : null}
          {subtitle && !primary ? (
            <span className="text-phx-sm text-phx-text-muted truncate">{subtitle}</span>
          ) : null}
        </div>
        {description && !primary ? (
          <div className="mt-phx-1 text-phx-sm text-phx-text-muted leading-relaxed">
            {description}
          </div>
        ) : null}
      </div>
      {onClose ? (
        <Button
          variant="ghost"
          size="icon-sm"
          iconLeft="x"
          aria-label={closeLabel}
          onClick={onClose}
          className={cn(
            'self-center',
            primary && 'text-phx-primary-fg hover:bg-phx-primary-hover hover:text-phx-primary-fg'
          )}
        />
      ) : null}
    </header>
  );
}
